import type { ExpenseCommand, FinancialSnapshot, Movement } from "./types";

const DB_NAME = "banculem-local-v1";
const DB_VERSION = 2;
const SNAPSHOT_STORE = "snapshots";
const OUTBOX_STORE = "outbox";
const ATTACHMENT_STORE = "attachments";
const SNAPSHOT_KEY = "current";
const BOOTSTRAP_KEY = "banculem:bootstrap:v1";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(SNAPSHOT_STORE)) database.createObjectStore(SNAPSHOT_STORE);
      if (!database.objectStoreNames.contains(OUTBOX_STORE)) database.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
      if (!database.objectStoreNames.contains(ATTACHMENT_STORE)) database.createObjectStore(ATTACHMENT_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function readBootstrapSnapshot(): FinancialSnapshot | null {
  try {
    const value = localStorage.getItem(BOOTSTRAP_KEY);
    const parsed = value ? (JSON.parse(value) as FinancialSnapshot) : null;
    return parsed?.schemaVersion === 3 ? parsed : null;
  } catch {
    return null;
  }
}

export async function readSnapshot(): Promise<FinancialSnapshot | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(SNAPSHOT_STORE, "readonly").objectStore(SNAPSHOT_STORE).get(SNAPSHOT_KEY);
    request.onsuccess = () => {
      const result = (request.result as FinancialSnapshot | undefined) ?? null;
      resolve(result?.schemaVersion === 3 ? result : null);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function saveSnapshot(snapshot: FinancialSnapshot): Promise<void> {
  localStorage.setItem(BOOTSTRAP_KEY, JSON.stringify(snapshot));
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(SNAPSHOT_STORE, "readwrite");
    transaction.objectStore(SNAPSHOT_STORE).put(snapshot, SNAPSHOT_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function enqueueMovements(movements: Movement[]): Promise<void> {
  if (!movements.length) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    const store = transaction.objectStore(OUTBOX_STORE);
    movements.forEach((movement) => store.put(movement));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function countPendingMovements(): Promise<number> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(OUTBOX_STORE, "readonly").objectStore(OUTBOX_STORE).count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function enqueueExpenseCommand(command: ExpenseCommand): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    transaction.objectStore(OUTBOX_STORE).put(command);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function listExpenseCommands(): Promise<ExpenseCommand[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(OUTBOX_STORE, "readonly").objectStore(OUTBOX_STORE).getAll();
    request.onsuccess = () => resolve(
      (request.result as Array<ExpenseCommand | Movement>)
        .filter((entry): entry is ExpenseCommand => "type" in entry && entry.type === "expense")
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    );
    request.onerror = () => reject(request.error);
  });
}

export async function removeOutboxItem(id: string): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    transaction.objectStore(OUTBOX_STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function saveExpenseCommandError(command: ExpenseCommand, error: string): Promise<void> {
  await enqueueExpenseCommand({ ...command, lastError: error.slice(0, 240) });
}

export async function saveAttachment(id: string, file: Blob): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(ATTACHMENT_STORE, "readwrite");
    transaction.objectStore(ATTACHMENT_STORE).put(file, id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function readAttachment(id: string): Promise<Blob | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(ATTACHMENT_STORE, "readonly").objectStore(ATTACHMENT_STORE).get(id);
    request.onsuccess = () => resolve((request.result as Blob | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function removeAttachment(id: string): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(ATTACHMENT_STORE, "readwrite");
    transaction.objectStore(ATTACHMENT_STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}
